import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  MobileStepper as Stepper,
  TextField,
  Typography,
} from "@mui/material";
import Shell from "components/shell";
import FormFieldContainer, {
  FormContainer,
  inputStyles,
} from "components/ui/FormField/FormFieldContainer";
import Page from "components/ui/Page/Page";
import { loginAPI, userProfile, verifyEmail } from "data";
import { useEnrollmentForm } from "hooks/useEnrollmentForm";
import { ENROLLMENT_USER_AUTH_DATA } from "constants/enrollment";

const Login = () => {
  const params = useParams();
  const SPONSOR_ID = params.sponsor.toUpperCase();
  const navigate = useNavigate();
  const [loginStep, setLoginStep] = useState(0);
  const [loginError, setLoginError] = useState(null); // For storing any error messages if needed in the future

  const { formData, handleFormChange, onSubmit, isSubmitting } =
    useEnrollmentForm({
      login: {
        email: "",
        password: "",
      },
    });

  const handleChange = (event) => {
    setLoginError(null);
    const { name, value } = event.target;
    handleFormChange("login", name, value);
  };

  const handleSubmit = () => {
    onSubmit(async () => {
      try {
        setLoginError(null);
        // Step 1: Login API call
        setLoginStep(1);
        const loginResponse = await loginAPI({
          email: formData.login.email,
          password: formData.login.password,
          sponsorSymbol: SPONSOR_ID,
        });
        const loginData = await loginResponse.json();
        if (!loginResponse.ok) {
          throw new Error(loginData?.errors?.[0]?.title || "Login failed");
        }

        // Step 2: Verify Email
        setLoginStep(2);
        const verifyEmailPromise = await verifyEmail({
          email: formData.login.email,
        });
        const verifyEmailData = await verifyEmailPromise.json();
        if (!verifyEmailPromise.ok || verifyEmailData?.errors) {
          throw new Error(
            verifyEmailData?.errors?.[0]?.title || "Email verification failed",
          );
        }

        // Step 3: Fetch User Profile
        setLoginStep(3);
        const userProgressPromise = await userProfile(loginData?.auth_token);
        const userProgressData = await userProgressPromise.json();
        if (userProgressData?.[0]?.stepID == "ShippingAddress") {
          throw new Error("You've already completed KardiaComplete enrollment");
        } else if (!userProgressPromise.ok) {
          throw new Error(
            userProgressData?.errors?.[0]?.title ||
              "User profile retrieval failed",
          );
        }

        setLoginStep(4);
        const authData = {
          auth_token: loginData?.auth_token,
          firstName: loginData.first_name, // This is needed to prefill the shipping form.
          lastName: loginData.last_name, // This is needed to prefill the shipping form.
        };
        localStorage.setItem(
          ENROLLMENT_USER_AUTH_DATA,
          JSON.stringify(authData),
        );
        navigate(`/register/${SPONSOR_ID}`);
      } catch (err) {
        setLoginError(err.message);
        setLoginStep(0);
        console.log(err);
      }
    });
  };

  const isSubmitDisabled =
    !formData.login.email || !formData.login.password || isSubmitting;

  return (
    <Shell
      Stepper={
        <Box sx={{ width: "100%", mb: 2 }}>
          <Stepper
            variant="progress"
            steps={5}
            position="static"
            activeStep={loginStep}
            sx={{
              width: "100%",
              flexGrow: 1,
              bgcolor: "transparent",
              boxShadow: "none",
              p: 0,
              "& .MuiMobileStepper-progress": {
                width: "100%",
              },
              "& .MuiLinearProgress-bar": {
                bgcolor: loginError ? "error.main" : "primary.main",
              },
            }}
          />
        </Box>
      }
    >
      <Page
        heading="Login"
        subHeading="Already have an account? Enter your log-in information below to complete registration."
      >
        <Typography sx={{ color: "#475569", fontSize: "16px" }}>
          Are you new to Kardia?{" "}
          <Link to={`/register/${SPONSOR_ID}`}>Create an Account</Link>.
        </Typography>

        <FormContainer
          sx={{
            width: "65%",
          }}
        >
          <FormFieldContainer
            heading="Email"
            required
            fields={[
              <TextField
                fullWidth
                name="email"
                placeholder="Email"
                value={formData.login.email}
                onChange={handleChange}
                disabled={isSubmitting}
                inputProps={{
                  autocomplete: "new-password",
                  form: {
                    autocomplete: "off",
                  },
                }}
                sx={inputStyles()}
              />,
            ]}
          />

          <FormFieldContainer
            heading="Password"
            required
            fields={[
              <TextField
                fullWidth
                name="password"
                placeholder="Password"
                type="password"
                value={formData.login.password}
                disabled={isSubmitting}
                onChange={handleChange}
                inputProps={{
                  autocomplete: "new-password",
                  form: {
                    autocomplete: "off",
                  },
                }}
                sx={inputStyles()}
              />,
            ]}
          />
          <LoadingButton
            variant="contained"
            loading={isSubmitting}
            disabled={isSubmitDisabled}
            onClick={handleSubmit}
          >
            Login
          </LoadingButton>
          {loginError && (
            <Typography sx={{ mt: 2, color: "error.main" }}>
              {loginError}
            </Typography>
          )}
        </FormContainer>
      </Page>
    </Shell>
  );
};

export default Login;
